import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import Loader from "../components/common/Loader";
import Modal from "../components/common/Modal";
import ServiceDetails from "../components/services/ServiceDetails";
import formatCurrency from "../utils/formatCurrency";
import * as serviceService from "../services/serviceService";

const safeCallGetServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }

  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }

  if (typeof serviceService.fetchServices === "function") {
    return serviceService.fetchServices();
  }

  throw new Error("Service list method was not found in serviceService.");
};

const shuffleArray = (array) => {
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

const isBridalService = (service) => {
  const name = service?.serviceName?.toLowerCase() || "";
  return name.includes("bridal henna") || name.includes("bridal");
};

const ServiceCardImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="hero-overlay flex h-56 items-center justify-center text-white">
        <p className="text-sm font-semibold uppercase tracking-[0.2em]">
          Nayra Henna
        </p>
      </div>
    );
  }

  return (
    <div className="h-56 w-full overflow-hidden bg-soft-cream">
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const Services = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "ALL");
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        setPageError("");

        const data = await safeCallGetServices();

        if (data?.statusCode && data.statusCode !== 200) {
          throw new Error(data.message || "Failed to load services.");
        }

        const serviceList = Array.isArray(data?.serviceList) ? data.serviceList : [];
        const activeServices = serviceList.filter((service) => service?.active !== false);

        setServices(activeServices);

        const serviceIdFromQuery = searchParams.get("serviceId");
        if (serviceIdFromQuery) {
          const matched = activeServices.find(
            (service) => Number(service.id) === Number(serviceIdFromQuery)
          );
          if (matched) {
            setSelectedService(matched);
          }
        }
      } catch (err) {
        setPageError(err?.message || "Unable to load services.");
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, [searchParams]);

  const serviceTypes = useMemo(() => {
    const types = new Set(
      services.map((service) => service?.serviceType).filter(Boolean)
    );
    return ["ALL", ...Array.from(types)];
  }, [services]);

  const filteredServices = useMemo(() => {
    const query = search.trim().toLowerCase();

    return services.filter((service) => {
      const matchesType =
        typeFilter === "ALL" ? true : service?.serviceType === typeFilter;

      const matchesSearch =
        !query ||
        service?.serviceName?.toLowerCase().includes(query) ||
        service?.serviceType?.toLowerCase().includes(query) ||
        service?.hennaType?.toLowerCase().includes(query) ||
        service?.serviceDescription?.toLowerCase().includes(query);

      return matchesType && matchesSearch;
    });
  }, [services, search, typeFilter]);

  const arrangedServices = useMemo(() => {
    const bridalServices = filteredServices.filter(isBridalService);
    const otherServices = filteredServices.filter((service) => !isBridalService(service));

    return [...bridalServices, ...shuffleArray(otherServices)];
  }, [filteredServices]);

  const updateQueryParams = (nextSearch, nextType) => {
    const params = new URLSearchParams();

    if (nextSearch?.trim()) {
      params.set("search", nextSearch.trim());
    }

    if (nextType && nextType !== "ALL") {
      params.set("type", nextType);
    }

    setSearchParams(params, { replace: true });
  };

  const handleOpenDetails = (service) => {
    setSelectedService(service);
  };

  const handleBookNow = (service) => {
    navigate(`/booking?serviceId=${service.id}`);
  };

  return (
    <div className="page-section">
      <PageContainer>
        <SectionTitle
          eyebrow="Henna Services"
          title="Find the service that feels right for you"
          subtitle="Browse available services, compare styles and pricing, and choose the option that best fits your occasion."
          className="mb-8"
        />

        <div className="content-card-lg p-6 md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-henna">Explore your options</h2>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                Search by service name, style, or description, or filter by
                service type to narrow your choices.
              </p>
            </div>

            <div className="flex flex-col gap-3 md:flex-row">
              <Input
                name="serviceSearch"
                value={search}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSearch(nextValue);
                  updateQueryParams(nextValue, typeFilter);
                }}
                placeholder="Search services..."
                className="min-w-[240px]"
              />

              <select
                value={typeFilter}
                onChange={(event) => {
                  const nextType = event.target.value;
                  setTypeFilter(nextType);
                  updateQueryParams(search, nextType);
                }}
                className="min-h-[50px] rounded-xl border border-goldlight bg-white px-4 py-3 text-sm text-[#2f2626]"
              >
                {serviceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type === "ALL" ? "All Types" : type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {pageError ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{pageError}</p>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-6">
            <Loader text="Loading services..." />
          </div>
        ) : arrangedServices.length === 0 ? (
          <div className="mt-6 content-card-lg p-8">
            <div className="empty-state">
              <p className="text-base font-medium text-[#3b2f2f]">
                No services matched your search.
              </p>
              <p className="mt-2 text-sm text-muted-warm">
                Try a different keyword or filter to see more options.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-6 responsive-three-col">
              {arrangedServices.map((service) => {
                const featured = isBridalService(service);

                return (
                  <article
                    key={service.id}
                    className={`content-card overflow-hidden transition-transform duration-200 hover:-translate-y-1 ${
                      featured ? "ring-2 ring-henna shadow-xl" : ""
                    }`}
                  >
                    <div className="relative">
                      {featured ? (
                        <div className="absolute left-4 top-4 z-10 rounded-full bg-henna px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow">
                          Featured
                        </div>
                      ) : null}

                      <ServiceCardImage
                        src={service?.serviceImageUrl}
                        alt={service?.serviceName || "Henna service"}
                      />
                    </div>

                    <div className="p-5">
                      <div className="flex flex-wrap gap-2">
                        {service?.serviceType ? (
                          <span className="badge-soft">{service.serviceType}</span>
                        ) : null}
                        {service?.hennaType ? (
                          <span className="badge-soft">{service.hennaType}</span>
                        ) : null}
                      </div>

                      <h3 className="mt-4 text-xl font-semibold text-henna">
                        {service?.serviceName || "Service"}
                      </h3>

                      <p className="mt-2 text-sm leading-6 text-muted-warm">
                        {service?.serviceDescription
                          ? service.serviceDescription.length > 120
                            ? `${service.serviceDescription.slice(0, 120)}...`
                            : service.serviceDescription
                          : "Henna service details will appear here."}
                      </p>

                      <div
                        className={`mt-5 flex items-center justify-between gap-3 border-t pt-4 ${
                          featured ? "border-henna/20" : "border-black/5"
                        }`}
                      >
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                            Starting Price
                          </p>
                          <p className="mt-1 text-lg font-semibold text-henna">
                            {service?.servicePrice !== undefined &&
                            service?.servicePrice !== null
                              ? formatCurrency(service.servicePrice)
                              : "Contact"}
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                            Duration
                          </p>
                          <p className="mt-1 text-lg font-semibold text-henna">
                            {service?.serviceDurationMinutes
                              ? `${service.serviceDurationMinutes} min`
                              : "—"}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-wrap gap-3">
                        <Button
                          variant="secondary"
                          onClick={() => handleOpenDetails(service)}
                        >
                          View Details
                        </Button>

                        <Button
                          variant="primary"
                          onClick={() => handleBookNow(service)}
                        >
                          Book This Service
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="mt-8 text-center">
              <p className="mx-auto max-w-2xl text-sm italic text-charcoal/70">
                Each henna design is uniquely crafted to reflect your personal
                style and occasion. Final pricing may vary slightly depending on
                design complexity and detail.
              </p>
            </div>
          </>
        )}

        <Modal
          isOpen={Boolean(selectedService)}
          onClose={() => setSelectedService(null)}
          title={selectedService?.serviceName || "Service details"}
        >
          {selectedService ? (
            <ServiceDetails
              service={selectedService}
              onBookNow={() => {
                navigate(`/booking?serviceId=${selectedService.id}`);
                setSelectedService(null);
              }}
            />
          ) : null}
        </Modal>
      </PageContainer>
    </div>
  );
};

export default Services;